/**
 * The ⓘ affordance beside a metric.
 *
 * Answers, for whatever number it sits next to: what it measures, the exact
 * formula, the window with its endpoints spelled out, what counts as
 * "possible", whether the still-open current period is included, and which
 * date it was evaluated against. That last line matters because an Analytics
 * Reference Date silently changes every other line's meaning.
 *
 * Content comes from the metrics glossary, never from prose written here, so
 * the popover cannot drift from the implementation or from the user guide.
 */
import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
  formatAnalyticsReferenceDateLong,
  useAnalyticsReferenceDate,
} from '../contexts/AnalyticsReferenceDateContext';
import { MetricContext, getMetricInfo } from '../metrics/metricsGlossary';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

interface MetricInfoProps {
  /** Glossary key, e.g. 'rolling_average'. Unknown keys render nothing. */
  metric: string;
  /** Window length in days, for metrics whose text is window-dependent. */
  days?: number;
  size?: 'small' | 'medium';
}

const Line: React.FC<{ heading: string; children: React.ReactNode; mono?: boolean }> = ({
  heading,
  children,
  mono,
}) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {heading}
    </Typography>
    <Typography
      variant="body2"
      sx={mono ? { fontFamily: 'monospace', fontSize: '0.8rem' } : undefined}
    >
      {children}
    </Typography>
  </Box>
);

const MetricInfo: React.FC<MetricInfoProps> = ({ metric, days, size = 'small' }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { asOfDate, enabled: referenceDateEnabled } = useAnalyticsReferenceDate();
  const { panelPaddingSx } = useLayoutTokens();

  const ctx: MetricContext | undefined = days === undefined ? undefined : { days };
  const info = getMetricInfo(metric, ctx);
  if (!info) {
    return null;
  }

  const evaluatedAgainst = referenceDateEnabled
    ? `Evaluated as of ${formatAnalyticsReferenceDateLong(asOfDate)} (Analytics Reference Date — a read-only lens; it never changes habit logging).`
    : 'Evaluated as of your local today.';

  return (
    <>
      <IconButton
        size={size}
        aria-label={`About ${info.label}`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ ml: 0.25 }}
      >
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={[panelPaddingSx, { maxWidth: 360 }]}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {info.label}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {info.what}
          </Typography>
          <Line heading="Formula" mono>
            {info.formula}
          </Line>
          <Line heading="Window">{info.window}</Line>
          <Line heading="What counts as possible">{info.denominator}</Line>
          <Line heading="Current period">
            {info.includes_open_period
              ? 'Includes the period in progress right now.'
              : 'Excludes the period in progress right now — only fully-elapsed periods count.'}
          </Line>
          <Typography variant="caption" color="text.secondary" display="block">
            {evaluatedAgainst}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default MetricInfo;
